<template>
  <div v-if="visible" class="modal-overlay">
    <div class="modal-content resizable-modal">
      <!-- Close button at top right -->
      <button class="modal-close" @click="onCancel" aria-label="Close">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
          stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
      <h2>Panel Settings</h2>
      <form @submit.prevent="onUpdate" class="settings-form">
        <div class="form-group">
          <label for="title">Title</label>
          <input id="title" v-model="form.title" type="text" required />
        </div>
        <div class="form-group">
          <label for="instructions">Instructions</label>
          <textarea id="instructions" v-model="form.instructions" rows="3"></textarea>
        </div>
        <div class="form-group">
          <label for="model">LLM Model</label>
          <select id="model" v-model="form.agentType">
            <option value="">Select a model...</option>
            <option v-for="model in availableModels" :key="model.id" :value="model.id">
              {{ model.displayName }} ({{ model.modelName }})
            </option>
          </select>
          <div v-if="selectedModelDescription" class="model-description">
            {{ selectedModelDescription }}
          </div>
        </div>
        <div class="form-group schedule-checkbox-row">
          <input type="checkbox" v-model="form.scheduleEnabled" id="scheduled-updates-checkbox"
            class="schedule-checkbox" />
          <label for="scheduled-updates-checkbox" class="schedule-label">Scheduled updates</label>
        </div>
        <div v-if="form.scheduleEnabled" class="form-group">
          <input id="schedule" v-model="form.scheduleText" type="text" placeholder="e.g. Every Monday at 9am"
            @input="updateCron" />
          <div v-if="form.cronString" class="schedule-help-text">
            <span v-if="humanSummary">{{ humanSummary }}</span>
          </div>
          <div v-else-if="form.scheduleText" class="schedule-error-text">
            Could not parse schedule.<br />
            <span class="schedule-usage-text">
              {{ usageString }}
            </span>
          </div>
        </div>
        <!-- Modal actions moved outside form for fixed positioning -->
      </form>

      <!-- Actions at lower right -->
      <div class="modal-actions-fixed">
        <button type="button" @click="onCancel">Cancel</button>
        <button type="button" @click="onUpdate">Update</button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch, PropType, computed, onMounted } from "vue";
import { naturalLanguageToCron, USAGE_STRING } from "@/composables/NLtoCron";
import { fetchModels, type LLMModel } from "@/services/api";
import cronstrue from "cronstrue";

export default defineComponent({
  name: "PanelSettingsEditor",
  props: {
    visible: { type: Boolean, required: true },
    initialSettings: {
      type: Object as PropType<{
        title: string;
        instructions: string;
        agentType?: string;
        schedule?: { enabled: boolean; humanText: string; cron: string } | string;
      }>,
      required: true,
    },
  },
  emits: ["update", "cancel"],
  setup(props, { emit }) {
    // Models state
    const availableModels = ref<LLMModel[]>([]);
    const modelsLoading = ref(false);
    
    // Normalize initial schedule
    const initialSchedule =
      typeof props.initialSettings.schedule === "object"
        ? (props.initialSettings.schedule as {
          enabled: boolean;
          humanText: string;
          cron: string;
        })
        : { enabled: false, humanText: "", cron: "" };
    const form = ref({
      title: props.initialSettings.title,
      instructions: props.initialSettings.instructions,
      agentType: props.initialSettings.agentType || "",
      scheduleEnabled: initialSchedule.enabled ?? false,
      scheduleText: initialSchedule.humanText ?? "",
      cronString: initialSchedule.cron ?? "",
    });

    // Computed property for selected model description
    const selectedModelDescription = computed(() => {
      if (!form.value.agentType) return "";
      const model = availableModels.value.find(m => m.id === form.value.agentType);
      return model?.description || "";
    });

    // Load available models
    async function loadModels() {
      try {
        modelsLoading.value = true;
        availableModels.value = await fetchModels();
      } catch (error) {
        console.error("Failed to load models:", error);
        // Set fallback models if API fails
        // Fallback models should conform to the LLMModel type from the backend
        availableModels.value = [
          { id: "azure:gpt-4.1", displayName: "GPT-4.1", modelName: "gpt-4.1", description: "Azure OpenAI" },
          { id: "azure:gpt-4o", displayName: "GPT-4o", modelName: "gpt-4o", description: "Azure OpenAI" }
        ];
      } finally {
        modelsLoading.value = false;
      }
    }

    // Load models when component mounts
    onMounted(() => {
      loadModels();
    });

    // Watch for prop changes
    watch(
      () => props.initialSettings,
      (newVal) => {
        const sched =
          typeof newVal.schedule === "object"
            ? (newVal.schedule as {
              enabled: boolean;
              humanText: string;
              cron: string;
            })
            : { enabled: false, humanText: "", cron: "" };
        form.value = {
          title: newVal.title,
          instructions: newVal.instructions,
          agentType: newVal.agentType || "",
          scheduleEnabled: sched.enabled ?? false,
          scheduleText: sched.humanText ?? "",
          cronString: sched.cron ?? "",
        };
      }
    );

    // Human summary of cron
    const humanSummary = computed(() => {
      if (form.value.cronString) {
        try {
          return cronstrue.toString(form.value.cronString);
        } catch {
          return "";
        }
      }
      return "";
    });

    // Update cron string when schedule text changes
    function updateCron() {
      if (!form.value.scheduleText) {
        form.value.cronString = "";
        return;
      }
      const cron = naturalLanguageToCron(form.value.scheduleText);
      form.value.cronString = cron || "";
    }

    const usageString = USAGE_STRING;

    const onUpdate = () => {
      emit("update", {
        title: form.value.title,
        instructions: form.value.instructions,
        agentType: form.value.agentType,
        schedule: form.value.scheduleEnabled
          ? {
            enabled: true,
            humanText: form.value.scheduleText,
            cron: form.value.cronString,
          }
          : { enabled: false, humanText: "", cron: "" },
      });
    };
    const onCancel = () => {
      emit("cancel");
    };
    return { 
      form, 
      onUpdate, 
      onCancel, 
      updateCron, 
      humanSummary, 
      usageString, 
      availableModels,
      selectedModelDescription
    };
  },
});
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.resizable-modal {
  background: #fff;
  padding: 2rem;
  border-radius: 12px; /* Match design spec border radius */
  min-width: 500px; /* Increase min width for better proportions */
  min-height: 400px;
  max-width: 90vw;
  max-height: 90vh;
  box-shadow: 0 4px 12px rgba(0,0,0,0.08); /* Softer shadow matching design */
  position: relative;
  resize: both;
  overflow: auto;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  font-family: 'Inter', sans-serif;
}

.modal-close {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #888;
  cursor: pointer;
  z-index: 10;
  transition: color 0.2s;
  padding: 0.25rem;
  border-radius: 4px;
}

.modal-close:hover {
  color: #333;
  background: #f5f5f5;
}

/* Modal title styling */
h2 {
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 1.25rem;
  color: #333333;
  margin: 0 0 1.5rem 0;
  padding-top: 0.5rem;
}

/* Form container with proper bottom padding */
.settings-form {
  padding-bottom: 5rem; /* Ensure form content doesn't overlap with fixed buttons */
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 0.875rem;
  color: #333333;
}

.form-group input,
.form-group textarea,
.form-group select {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #E5E5E5;
  border-radius: 8px;
  font-family: 'Inter', sans-serif;
  font-size: 0.875rem;
  color: #444444;
  background: #fff;
  transition: border-color 0.2s, box-shadow 0.2s;
  box-sizing: border-box; /* Ensure consistent width calculation */
}

.form-group input:focus,
.form-group textarea:focus,
.form-group select:focus {
  outline: none;
  border-color: #0078D4;
  box-shadow: 0 0 0 3px rgba(0, 120, 212, 0.1);
}

/* Schedule help text styling */
.schedule-help-text {
  margin-top: 0.5rem;
  color: #666;
  font-size: 0.8rem;
  line-height: 1.3;
}

.schedule-error-text {
  color: #c00;
  font-size: 0.8rem;
  margin-top: 0.5rem;
  line-height: 1.3;
}

.schedule-usage-text {
  color: #666;
  font-size: 0.75rem;
  white-space: pre-line;
  display: block;
  margin-top: 0.25rem;
}

.model-description {
  margin-top: 0.5rem;
  color: #666;
  font-size: 0.9rem;
  font-style: italic;
}

/* Remove old .modal-actions, use fixed actions instead */
.modal-actions-fixed {
  position: absolute;
  right: 2rem;
  bottom: 2rem;
  display: flex;
  gap: 1rem;
  z-index: 5;
}

/* Decorative resize icon, not interactive. Native resize is handled by browser. */
.resize-handle {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 12px;
  height: 12px;
  margin: 2px;
  cursor: nwse-resize;
  z-index: 20;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 2px 0 0 0;
  pointer-events: none;
  /* Let native resize handle work */
}

.resize-handle::after {
  content: "";
  display: block;
  width: 100%;
  height: 100%;
  background: url('data:image/svg+xml;utf8,<svg width="12" height="12" xmlns="http://www.w3.org/2000/svg"><path d="M1 11 L11 1 M3 11 L11 3 M5 11 L11 5" stroke="%23999" stroke-width="1.5" fill="none"/></svg>') no-repeat center center;
  opacity: 0.7;
}

.schedule-checkbox-row {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  padding-left: 0;
}

.schedule-checkbox {
  width: auto !important;
  margin-right: 0.75em;
  margin-left: 0;
  align-self: center;
  vertical-align: middle;
  position: relative;
  top: -4px;
  /* Adjusted for better centering */
}

.schedule-label {
  font-weight: 500;
  font-size: 1rem;
  margin: 0;
  display: flex;
  align-items: center;
  white-space: nowrap;
  height: auto;
}

/* Modal action buttons */
.modal-actions-fixed {
  position: absolute;
  right: 2rem;
  bottom: 2rem;
  display: flex;
  gap: 0.75rem;
  z-index: 5;
}

.modal-actions-fixed button {
  padding: 0.5rem 1rem;
  border: 1px solid #E5E5E5;
  border-radius: 6px;
  font-family: 'Inter', sans-serif;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  min-width: 80px;
}

.modal-actions-fixed button[type="button"]:first-child {
  background: #fff;
  color: #444444;
}

.modal-actions-fixed button[type="button"]:first-child:hover {
  background: #F7F7F7;
  border-color: #D1D5DB;
}

.modal-actions-fixed button[type="button"]:last-child {
  background: #0078D4;
  color: #fff;
  border-color: #0078D4;
}

.modal-actions-fixed button[type="button"]:last-child:hover {
  background: #106EBE;
  border-color: #106EBE;
}
</style>
